import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../../context/AppContext';
import {
  pdfToArrayBuffer,
  getPdfPageCount,
  validateDocumentFile
} from '../../utils/pdfUtils';
import { Document } from '../../types';

export function DocumentUpload() {
  const { dispatch } = useApp();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    const validationError = validateDocumentFile(file);

    if (validationError) {
      dispatch({ type: 'SET_ERROR', payload: validationError });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Direct PDF upload only
      const pdfData = await pdfToArrayBuffer(file);

      const pageCount = await getPdfPageCount(pdfData);
      console.log('Document uploaded - Page count:', pageCount, 'File:', file.name);

      const document: Document = {
        id: Date.now().toString(),
        name: file.name.replace(/\.(docx?|pdf)$/i, '.pdf'), // Ensure .pdf extension
        pdfData,
        pageCount,
        uploadedAt: new Date(),
      };

      console.log('Created document object:', { id: document.id, name: document.name, pageCount: document.pageCount });
      dispatch({ type: 'SET_DOCUMENT', payload: document });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Onbekende fout bij uploaden'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Document Uploaden</h2>
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          bg-white dark:bg-black
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
            : 'border-gray-300 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-900'
          }
        `}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center space-y-4">
          <svg
            className="w-12 h-12 text-gray-400 dark:text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          {isDragActive ? (
            <p className="text-blue-600 dark:text-blue-400 font-medium">
              Sleep je bestand hier naartoe...
            </p>
          ) : (
            <div>
              <p className="text-gray-600 dark:text-white font-medium mb-2">
                Sleep een PDF bestand hierheen of klik om te selecteren
              </p>
              <p className="text-gray-400 dark:text-blue-400 text-sm">
                Maximaal 10MB - Alleen PDF bestanden
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}